import type {
  AdminDashboardSummary,
  DashboardGiftMetrics,
  DashboardKycMetrics,
  DashboardTransactionMetrics,
  DashboardWindow
} from "../types";
import { giftWasFunded } from "./giftOps";
import { kycStage } from "./kycOps";
import { TRANSACTION_FAILURE_STATES, TRANSACTION_STALE_STATES } from "./transactionOps";

export const DASHBOARD_WINDOWS: ReadonlyArray<{ key: DashboardWindow; label: string; milliseconds: number }> = [
  { key: "1d", label: "Last 24 hours", milliseconds: 86_400_000 },
  { key: "7d", label: "Last 7 days", milliseconds: 7 * 86_400_000 },
  { key: "30d", label: "Last 30 days", milliseconds: 30 * 86_400_000 }
];

const sum = (counts: Record<string, number>, predicate: (raw: string) => boolean = () => true) =>
  Object.entries(counts).reduce((total, [raw, count]) => total + (predicate(raw) ? count : 0), 0);

export function summarizeKyc(metrics: DashboardKycMetrics) {
  return {
    needsAttention: sum(metrics.currentByRawState, (raw) => ["customerAction", "blocked", "unknown"].includes(kycStage(raw))),
    unmapped: sum(metrics.currentByRawState, (raw) => kycStage(raw) === "unknown")
  };
}

export function summarizeGifts(metrics: DashboardGiftMetrics) {
  return {
    currentTotal: sum(metrics.totalByRawState, giftWasFunded),
    createdInWindow: sum(metrics.createdInWindowByRawState, giftWasFunded),
    unmapped: sum(metrics.totalByRawState, (raw) => !giftWasFunded(raw) && !["creating", "failed"].includes(raw.trim().toLowerCase()))
  };
}

export function summarizeTransactions(metrics: DashboardTransactionMetrics) {
  const failureStates = new Set<string>(TRANSACTION_FAILURE_STATES);
  const staleStates = new Set<string>(TRANSACTION_STALE_STATES);
  return {
    createdInWindow: sum(metrics.createdInWindowByRawStatus),
    failuresUpdatedInWindow: sum(metrics.updatedInWindowByRawStatus, (raw) => failureStates.has(raw.trim().toLowerCase())),
    staleCurrent: sum(metrics.staleBefore24hByRawStatus, (raw) => staleStates.has(raw.trim().toLowerCase()))
  };
}

export function unavailableDashboard(window: DashboardWindow, now = new Date()): AdminDashboardSummary {
  const duration = DASHBOARD_WINDOWS.find((item) => item.key === window)?.milliseconds ?? 7 * 86_400_000;
  return {
    generatedAt: now.toISOString(),
    window: { key: window, startsAt: new Date(now.getTime() - duration).toISOString(), endsAt: now.toISOString() },
    registrations: { available: false, reason: "not_indexed" },
    operations: { available: false, reason: "unavailable" },
    kyc: { available: false, reason: "not_deployed" },
    gifts: { available: false, reason: "not_deployed" },
    transactions: { available: false, reason: "not_deployed" }
  };
}
